import { Component, inject, Input, OnInit } from '@angular/core';
import { ICON } from '@core/util/icon';
import {
	CondoUnit,
	MatterPropertyType,
	MatterTitle,
	PropertyType,
	UnitType,
} from '@models/matter';
import { PropertyUnitsModalComponent } from '../property-units-modal/property-units-modal.component';
import { MatterTitlesDispatch } from '@state/matter/titles/titles.dispatch';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TestHooks } from './property-unit-overview.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { Logger } from '@core/logger';
import { SnackbarService } from '@services/ui/snackbar.service';

enum KebabOptions {
	EDIT = 'edit',
	REMOVE = 'remove',
}
@UntilDestroy()
@Component({
	selector: 'app-property-unit-overview',
	templateUrl: './property-unit-overview.component.html',
	standalone: false,
})
export class PropertyUnitOverviewComponent implements OnInit {
	private store = inject(Store);

	testHooks = TestHooks;
	@Input() title: MatterTitle;

	public ICON = ICON;

	public additionalKebabOptions: DropdownOption<KebabOptions>[] = [
		{ name: 'Edit', value: KebabOptions.EDIT },
		{ name: 'Remove', value: KebabOptions.REMOVE, classes: ['text-red'] },
	];

	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;

	@SelectSnapshot(SelectedMatter.matterHST) hstPercentage: number;

	public commonExpensesAdjustments = this.store.selectSignal(
		AdjustmentsSelector.commonExpensesAdjustment,
	);

	public isCondo = false;

	constructor(
		private matterTitleDispatch: MatterTitlesDispatch,
		private drawerService: DrawerService,
		private adjustmentDispatch: AdjustmentsDispatch,
		private snackbar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.propertyType$.pipe(untilDestroyed(this)).subscribe((propertyType) => {
			this.isCondo = propertyType?.type === PropertyType.CONDO;
		});
	}
	openEditModal(): void {
		this.drawerService.openDrawer(PropertyUnitsModalComponent, {
			size: 'sm',
			title: `Edit ${this.isCondo ? 'Unit' : 'Parcel'}`,
			noStyle: true,
			data: {
				title: this.title,
				titleId: this.title.id,
			},
		});
	}

	delete(titleId: string) {
		if ((this.title as CondoUnit).linkedSoAdjCommonExpensesId) {
			this.deleteLinkedCommonExpensesAdjustment(
				(this.title as CondoUnit).linkedSoAdjCommonExpensesId,
			);
		}

		this.matterTitleDispatch.Remove(titleId);
	}

	deleteLinkedCommonExpensesAdjustment(
		linkedCommonExpensesAdjustmentId: string,
	) {
		if (!linkedCommonExpensesAdjustmentId) return;

		const linkedCommExpAdjustment = this.commonExpensesAdjustments().find(
			(x) => x.id === linkedCommonExpensesAdjustmentId,
		);

		if (!linkedCommExpAdjustment) {
			Logger.error(
				`Unable to delete linked common expenses adjustment item. Expected to find common expenses adjustment id ${linkedCommonExpensesAdjustmentId} but no adjustment matching that id was found.`,
			);
			return;
		}

		this.adjustmentDispatch.Remove(linkedCommExpAdjustment);

		this.snackbar.openSnackBar({
			text: 'Common expenses updated and will reflect on statement of adjustments.',
			icon: ICON.CIRCLE_CHECK,
			type: 'success',
		});
	}

	onKebabOptionSelected(option: DropdownResult<DropdownOption<KebabOptions>>) {
		switch (option.result.value) {
			case KebabOptions.EDIT:
				this.openEditModal();
				break;
			case KebabOptions.REMOVE:
				this.delete(this.title.id);
				break;
		}
	}

	getCommonExpensesTotal() {
		const condoTitle = this.title as CondoUnit;
		const commonExpenses = condoTitle?.commonExpenses ?? 0;

		if (
			condoTitle?.unitType === UnitType.COMMERCIAL_UNIT &&
			condoTitle?.applyHst
		) {
			return Math.round(commonExpenses + commonExpenses * this.hstPercentage);
		} else {
			return commonExpenses;
		}
	}
}
